import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Drawer, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import crmStandupService, { type StandupMeeting, type StandupMeetingDetail } from '@/services/crmStandup.service';
import { formatDate } from '@/utils/format/date';

const { Title, Paragraph, Text } = Typography;

const StandupHistoryPage: React.FC = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: meetings = [], isLoading, error } = useQuery({
    queryKey: ['crm-standup-meetings', 'Finalized'],
    queryFn: () => crmStandupService.listMeetings({ status: 'Finalized', limit: 60 }),
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['crm-standup-meeting', selectedId],
    queryFn: () => crmStandupService.getMeeting(selectedId!),
    enabled: Boolean(selectedId),
  });

  const columns: ColumnsType<StandupMeeting> = [
    {
      title: 'Date',
      dataIndex: 'meeting_date',
      render: (v: string) => formatDate(v),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v: string) => <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Finalized',
      dataIndex: 'finalized_at',
      render: (v?: string | null) => (v ? formatDate(v) : '—'),
    },
    {
      title: '',
      render: (_, row) => (
        <Button type="link" onClick={() => setSelectedId(row.id)}>
          View snapshot
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Stand-Up history
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Finalized meetings and immutable snapshots.
          </Paragraph>
        </div>
        <Space>
          <Button>
            <Link to="/growth/standup">My brief</Link>
          </Button>
          <Button>
            <Link to="/growth/standup/team">Team console</Link>
          </Button>
        </Space>
      </Space>

      {error && (
        <Alert type="error" showIcon message={(error as any)?.response?.data?.message || 'Load failed'} />
      )}

      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={meetings}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Drawer
        width={720}
        title={detail ? `Stand-up ${formatDate(detail.meeting.meeting_date)}` : 'Snapshot'}
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        destroyOnClose
      >
        {detailLoading && <Text type="secondary">Loading…</Text>}
        {detail && <SnapshotViewer detail={detail} />}
      </Drawer>
    </Space>
  );
};

function SnapshotViewer({ detail }: { detail: StandupMeetingDetail }) {
  const payload = detail.snapshot?.payload_json as any;
  const summary = detail.meeting.summary_text || payload?.summary?.summary_text;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small" title="Published summary">
        <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>{summary || '—'}</pre>
      </Card>
      <Card size="small" title="Participants">
        {(detail.participants || []).map((p) => (
          <div key={p.id}>
            <Text code>{p.user_id.slice(0, 8)}</Text> · {p.attendance}
            {p.submitted_at ? ` · submitted ${formatDate(p.submitted_at)}` : ''}
          </div>
        ))}
      </Card>
      <Card size="small" title="Answers">
        {(detail.answers || []).map((a) => (
          <div key={a.id} style={{ marginBottom: 8 }}>
            <Text strong>{detail.question_labels?.[a.question_key] || a.question_key}</Text>
            <div>{a.answer_text || '—'}</div>
          </div>
        ))}
        {!detail.answers?.length && <Text type="secondary">No answers stored</Text>}
      </Card>
      <Card size="small" title="Decisions">
        {(detail.decisions || []).map((d) => (
          <div key={d.id}>
            {d.decision_text}
            {d.due_date ? ` (due ${formatDate(d.due_date)})` : ''}
            {d.crm_activity_id ? (
              <>
                {' '}
                <Link to={`/growth/follow-ups?activity=${d.crm_activity_id}`}>Follow-up</Link>
              </>
            ) : null}
          </div>
        ))}
        {!detail.decisions?.length && <Text type="secondary">No decisions</Text>}
      </Card>
    </Space>
  );
}

export default StandupHistoryPage;
